import {
  Component,
  Host,
  h,
  State,
  getAssetPath,
  Prop,
  Listen,
} from "@stencil/core";
import * as THREE from "three";
import { ColladaLoader } from "three/examples/jsm/loaders/ColladaLoader.js";
import {
  IElevatorMaterials,
  ILiftStatusWebComponentError,
  ILiftStatusSensorDataResponse,
  TMovementSensor,
} from "../../utils/types";
import {
  DoorPosition,
  MovementState,
  SCENE_SENSOR_IDS,
  SENSOR,
} from "../../utils/sensors";
import { ElevatorDirection, ErrorType } from "../../utils/status";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/nl";
import { getStore, ILiftStatusStore } from "../../store";
import { isNil } from "lodash-es";
import { Easing, Tween } from "@tweenjs/tween.js";
import { getSafeRelativeTime } from "../../utils/safe-relative-time";
import {
  applyErrorMessage,
  clearErrorMessage,
  getErrorMessageForDisplay,
} from "../../utils/error-message";
import { watchStoreLocale } from "../../utils/locale-state";
import { getStoreUnavailableError } from "../../utils/store-error";

@Component({
  tag: "ls-3d-scene",
  styleUrl: "ls-3d-scene.css",
  shadow: true,
  assetsDirs: ["assets"],
})
export class Ls3dScene {
  /**
   * The unique key that is used to identify store data.
   */
  @Prop() readonly idKey: string;

  /**
   * Elevator state (movement)
   */
  @State() movementState: TMovementSensor;

  /**
   * Current stop
   */
  @State() currentStop: number | string;

  /**
   * Last passed stop
   */
  @State() lastPassedStop: number;

  /**
   * The timestamp of the latest sensor update
   */
  @State() lastUpdate: number;

  /**
   * Centralized error state for tracking component errors
   */
  @State() errorState: ILiftStatusWebComponentError = {
    hasError: false,
    messageKey: undefined,
    type: ErrorType.None,
  };

  /**
   * Keeps track of the setInterval ID.
   */
  private timer: number;
  private hasBackDoorTimeout: NodeJS.Timeout | null = null;
  private isComponentMounted = false;

  private store: ILiftStatusStore | null = null;
  private storeUnsubscribe: (() => void) | null = null;

  private animateTheFloor = false;
  private isAnimating = false;
  private isEasingOut = false;
  private isEasingIn = false;
  private animationFrameId: number | null = null;
  private arrowDown: THREE.Object3D;
  private arrowUp: THREE.Object3D;
  private backDoor: THREE.Object3D;
  private backDoorLeft = new THREE.Object3D();
  private backDoorRight = new THREE.Object3D();
  private camera: THREE.PerspectiveCamera;
  private canvas: HTMLDivElement;
  private doorWidth = 12.5;
  private elevator: THREE.Scene;
  private elevatorBackWall: THREE.Object3D;
  private elevatorBackWallBeam: THREE.Object3D;
  private floorAnimationTween: Tween<{ topPos: number }> | null = null;
  private doorAnimationTween: Tween<unknown> | null = null;
  private floorHeight = 500;
  private floorSeparatorIndicator = new THREE.Group();
  private frontDoorLeft = new THREE.Object3D();
  private frontDoorRight = new THREE.Object3D();
  private movingDirection: ElevatorDirection = ElevatorDirection.Down;
  private pulseInterval: NodeJS.Timeout | null = null;
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private speed = 3000;
  private readonly DEFAULT_SCENE_SIZE = 500;
  private resizeObserver: ResizeObserver | null = null;
  private resizeFrameId: number | null = null;
  private lastCanvasSize = { width: 0, height: 0 };

  private positions = {
    frontDoor: {
      left: {
        open: 0,
        closed: 0,
      },
      right: {
        open: 0,
        closed: 0,
      },
    },
    backDoor: {
      left: {
        open: 0,
        closed: 0,
      },
      right: {
        open: 0,
        closed: 0,
      },
    },
    floor: {
      down: 0,
      up: 0,
    },
  };

  private materials: IElevatorMaterials = {
    active: new THREE.MeshStandardMaterial({ color: 0xff0000, opacity: 1 }),
    normal: new THREE.MeshStandardMaterial({
      color: 0xa3a3a3,
      opacity: 1,
      transparent: false,
    }),
    pulseOn: new THREE.MeshStandardMaterial({
      color: 0x024970,
      opacity: 0.5,
      transparent: true,
    }),
    pulseOff: new THREE.MeshStandardMaterial({
      color: 0x024970,
      opacity: 1,
      transparent: false,
    }),
    transparent: new THREE.MeshStandardMaterial({
      color: 0xffffff,
      opacity: 0,
      transparent: true,
    }),
    floor: new THREE.MeshStandardMaterial({
      color: 0x85b19b,
      opacity: 0.15,
      transparent: true,
    }),
    door: new THREE.MeshStandardMaterial({
      color: 0xff0000,
      emissive: 0x0,
      roughness: 0,
      metalness: 1,
    }),
  };

  private initializeScene = () => {
    // Clean up existing scene if it exists
    this.cleanupScene();

    // Re-initialize materials after cleaning
    this.materials = {
      active: new THREE.MeshStandardMaterial({ color: 0xff0000, opacity: 1 }),
      normal: new THREE.MeshStandardMaterial({
        color: 0xa3a3a3,
        opacity: 1,
        transparent: false,
      }),
      pulseOn: new THREE.MeshStandardMaterial({
        color: 0x024970,
        opacity: 0.5,
        transparent: true,
      }),
      pulseOff: new THREE.MeshStandardMaterial({
        color: 0x024970,
        opacity: 1,
        transparent: false,
      }),
      transparent: new THREE.MeshStandardMaterial({
        color: 0xffffff,
        opacity: 0,
        transparent: true,
      }),
      floor: new THREE.MeshStandardMaterial({
        color: 0x85b19b,
        opacity: 0.15,
        transparent: true,
      }),
      door: new THREE.MeshStandardMaterial({
        color: 0xff0000,
        emissive: 0x0,
        roughness: 0,
        metalness: 1,
      }),
    };

    const { width, height } = this.getCanvasSize();

    // Setup the camera
    this.camera = new THREE.PerspectiveCamera(45, width / height, 1, 1000);
    this.camera.position.set(7, 4, 3.5);
    this.camera.zoom = 1.7;
    this.camera.updateProjectionMatrix();
    this.camera.lookAt(0, 0, 0);

    // Create the scene
    this.scene = new THREE.Scene();

    // Initialize the loading manager
    const loadingManager = new THREE.LoadingManager(() => {
      // If 3D scene file failed to load
      if (this.elevator === undefined) {
        const error = document.createElement("div");
        error.textContent = this.store.t("ui:scene.failed_to_load");
        error.classList.add("failed-to-load-error");

        // On reload: replace the current scene, instead of creating a new one
        if (this.canvas.children.length > 2) {
          this.canvas.replaceChild(error, this.canvas.children[2]);
        } else {
          this.canvas.appendChild(error);
        }
      } else {
        this.scene.add(this.elevator);
      }
    });

    // Load the elevator.dae file and initialize the objects
    const loader = new ColladaLoader(loadingManager);
    loader.load(getAssetPath("./assets/elevator-001.dae"), (collada) => {
      this.elevator = collada.scene;

      this.arrowUp = this.elevator.getObjectByName("arrow_up");
      this.arrowDown = this.elevator.getObjectByName("arrow_down");
      this.elevatorBackWall =
        this.elevator.getObjectByName("elevator_back_wall");
      this.elevatorBackWallBeam =
        this.elevator.getObjectByName("achter_wand_beam");
      this.frontDoorLeft = this.elevator.getObjectByName("front_door_left");
      this.frontDoorRight = this.elevator.getObjectByName("front_door_right");
      this.backDoor = this.elevator.getObjectByName("back_door");
      this.backDoorLeft = this.elevator.getObjectByName("back_door_left");
      this.backDoorRight = this.elevator.getObjectByName("back_door_right");
      this.floorSeparatorIndicator = this.elevator.getObjectByName(
        "floor_separator_indicator",
      ) as THREE.Group;

      // Hide outlines
      this.elevator.children[0].traverse((childNode: THREE.LineSegments) => {
        if (childNode.type === "LineSegments") {
          childNode.visible = false;
        }
      });

      // Change the material for the arrows
      for (let i = 0; i < this.arrowDown.children.length; i++) {
        this.changeMaterial(
          this.arrowDown.children[i],
          this.materials.pulseOff,
        );
        this.changeMaterial(this.arrowUp.children[i], this.materials.pulseOff);
      }

      // Hide arrows
      this.stopMoving();

      // Assign positions
      this.positions = {
        frontDoor: {
          left: {
            open: this.frontDoorLeft.position.y + this.doorWidth,
            closed: this.frontDoorLeft.position.y,
          },
          right: {
            open: this.frontDoorRight.position.y - this.doorWidth,
            closed: this.frontDoorRight.position.y,
          },
        },
        backDoor: {
          left: {
            open: this.backDoorLeft.position.y + this.doorWidth,
            closed: this.backDoorLeft.position.y,
          },
          right: {
            open: this.backDoorRight.position.y - this.doorWidth,
            closed: this.backDoorRight.position.y,
          },
        },
        floor: {
          down: this.floorSeparatorIndicator.position.z,
          up: this.floorSeparatorIndicator.position.z + this.floorHeight,
        },
      };

      const snapshot = this.store?.state?.currentSensorData;
      if (snapshot !== undefined && snapshot !== null) {
        this.processSensorUpdate(snapshot, true);

        // Ensure initial movement state is visually applied once the model is ready.
        const initialMovement = snapshot.values?.[SENSOR.MovementState] as
          | TMovementSensor
          | undefined;
        if (initialMovement === MovementState.Up) {
          this.moveUp();
        } else if (initialMovement === MovementState.Down) {
          this.moveDown();
        } else {
          this.stopMoving();
        }
      }
    });

    // Setup lighting
    const ambientLight = new THREE.AmbientLight(0xcccccc, 1);
    this.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xeeeeee, 2);
    directionalLight.position.set(1, 1, 0).normalize();
    this.scene.add(directionalLight);

    // Setup renderer
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setClearColor(0x000000, 0);

    // On reload: replace the current scene, instead of creating a new one
    if (this.canvas.children.length > 3) {
      this.canvas.replaceChild(
        this.renderer.domElement,
        this.canvas.children[3],
      );
    } else {
      this.canvas.appendChild(this.renderer.domElement);
    }

    this.resizeScene();
  };

  /*
   * Functions
   */
  @Listen("resize", { target: "window" })
  scheduleSceneResizeOnWindowResize() {
    if (!this.isComponentMounted) {
      return;
    }

    if (this.resizeObserver === null) {
      this.scheduleResize();
    }
  }

  private scheduleResize = () => {
    if (this.resizeFrameId !== null) {
      return;
    }

    this.resizeFrameId = requestAnimationFrame(() => {
      this.resizeFrameId = null;
      this.resizeScene();
    });
  };

  private observeResize = () => {
    const ResizeObserverConstructor = (
      window as Window & { ResizeObserver?: typeof ResizeObserver }
    ).ResizeObserver;

    if (isNil(this.canvas) || ResizeObserverConstructor === undefined) {
      return;
    }

    this.resizeObserver = new ResizeObserverConstructor(() => {
      if (!this.isComponentMounted) {
        return;
      }
      this.scheduleResize();
    });
    this.resizeObserver.observe(this.canvas);
  };

  private getCanvasSize = () => {
    const fallbackSize = this.DEFAULT_SCENE_SIZE;

    if (isNil(this.canvas)) {
      return { width: fallbackSize, height: fallbackSize };
    }

    const rect = this.canvas.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width || fallbackSize));
    const height = Math.max(1, Math.round(rect.height || width));

    return { width, height };
  };

  private resizeScene = () => {
    if (
      isNil(this.renderer) ||
      isNil(this.camera) ||
      isNil(this.canvas)
    ) {
      return;
    }

    const { width, height } = this.getCanvasSize();
    if (
      width === this.lastCanvasSize.width &&
      height === this.lastCanvasSize.height
    ) {
      return;
    }

    this.lastCanvasSize = { width, height };
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.updateIndicatorScale(width);
    this.renderScene();
  };

  private updateIndicatorScale = (canvasWidth: number) => {
    if (isNil(this.canvas)) {
      return;
    }

    const baseSize = this.DEFAULT_SCENE_SIZE;
    const scale = Math.min(1, canvasWidth / baseSize);
    const fontSizePx =
      Number.parseFloat(getComputedStyle(this.canvas).fontSize) || 16;

    const toPx = (value: number, min = 1) =>
      `${Math.max(min, Math.round(value))}px`;

    this.canvas.style.setProperty("--ls-indicator-width", toPx(150 * scale));
    this.canvas.style.setProperty("--ls-indicator-height", toPx(96 * scale));
    this.canvas.style.setProperty(
      "--ls-indicator-label-size",
      toPx(fontSizePx * scale),
    );
    this.canvas.style.setProperty(
      "--ls-indicator-label-line-height",
      toPx(fontSizePx * 1.5 * scale),
    );
    this.canvas.style.setProperty(
      "--ls-indicator-digit-size",
      toPx(fontSizePx * 5 * scale),
    );
    this.canvas.style.setProperty(
      "--ls-indicator-digit-line-height",
      toPx(80 * scale),
    );
    this.canvas.style.setProperty(
      "--ls-indicator-digit-width",
      toPx(75 * scale),
    );
    this.canvas.style.setProperty(
      "--ls-indicator-letter-spacing",
      toPx(fontSizePx * 0.05 * scale, 0),
    );

    this.canvas.style.setProperty(
      "--ls-3d-scene-last-update-size",
      toPx(fontSizePx * scale),
    );
    this.canvas.style.setProperty(
      "--ls-3d-scene-last-update-line-height",
      toPx(fontSizePx * 1.5 * scale),
    );
    this.canvas.style.setProperty(
      "--ls-3d-scene-last-update-padding",
      `${toPx(fontSizePx * 0.25 * scale)} ${toPx(fontSizePx * 0.5 * scale)}`,
    );
    this.canvas.style.setProperty(
      "--ls-3d-scene-last-update-radius",
      toPx(20 * scale),
    );
  };

  private animateScene = () => {
    if (!this.isAnimating || !this.isComponentMounted) {
      this.animationFrameId = null;
      return;
    }

    // Update tweens only if they exist
    if (this.doorAnimationTween !== null) {
      this.doorAnimationTween.update();
    }
    if (this.floorAnimationTween !== null) {
      this.floorAnimationTween.update();
    }

    this.renderScene();

    // Schedule next frame
    this.animationFrameId = requestAnimationFrame(this.animateScene);
  };

  private startAnimation = () => {
    if (!this.isAnimating) {
      this.isAnimating = true;
      this.animateScene();
    }
  };

  private stopAnimation = () => {
    this.isAnimating = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  };

  private renderScene = () => {
    this.renderer?.render(this.scene, this.camera);
  };

  private stopDoorAnimation = () => {
    if (this.doorAnimationTween !== null) {
      if (typeof this.doorAnimationTween.stop === "function") {
        this.doorAnimationTween.stop();
      }
      this.doorAnimationTween = null;
    }
  };

  private changeMaterial = (
    node: THREE.Object3D,
    material: THREE.MeshStandardMaterial,
  ) => {
    if (
      typeof node.traverse !== "function" ||
      !this.isComponentMounted
    ) {
      return;
    }

    node.traverse((childNode: THREE.Object3D) => {
      if (childNode.type === "LineSegments") {
        childNode.visible = false;
      } else if (childNode instanceof THREE.Mesh) {
        childNode.material = material;
      }
    });
  };

  private openFrontDoor = (skipAnimation = false) => {
    if (
      isNil(this.frontDoorLeft) || isNil(this.frontDoorRight)
    ) {
      return;
    }

    if (this.frontDoorLeft.position.y === this.positions.frontDoor.left.open) {
      return;
    }

    if (skipAnimation) {
      this.frontDoorLeft.position.y = this.positions.frontDoor.left.open;
      this.frontDoorRight.position.y = this.positions.frontDoor.right.open;
      return;
    }

    // Stop and clean up any existing door animation
    this.stopDoorAnimation();

    // Ensure main animation loop is running for tween updates
    this.startAnimation();

    this.doorAnimationTween = new Tween({
      leftDoor: this.frontDoorLeft.position.y,
      rightDoor: this.frontDoorRight.position.y,
    })
      .to(
        {
          leftDoor: this.positions.frontDoor.left.open,
          rightDoor: this.positions.frontDoor.right.open,
        },
        this.speed,
      )
      .easing(Easing.Cubic.InOut)
      .onUpdate(({ leftDoor, rightDoor }) => {
        this.frontDoorLeft.position.y = leftDoor;
        this.frontDoorRight.position.y = rightDoor;
      })
      .start();
  };

  private closeFrontDoor = (skipAnimation = false) => {
    if (
      isNil(this.frontDoorLeft) || isNil(this.frontDoorRight)
    ) {
      return;
    }

    if (
      this.frontDoorLeft.position.y === this.positions.frontDoor.left.closed
    ) {
      return;
    }

    if (skipAnimation) {
      this.frontDoorLeft.position.y = this.positions.frontDoor.left.closed;
      this.frontDoorRight.position.y = this.positions.frontDoor.right.closed;
      return;
    }

    // Stop and clean up any existing door animation
    this.stopDoorAnimation();

    // Ensure main animation loop is running for tween updates
    this.startAnimation();

    this.doorAnimationTween = new Tween({
      leftDoor: this.frontDoorLeft.position.y,
      rightDoor: this.frontDoorRight.position.y,
    })
      .to(
        {
          leftDoor: this.positions.frontDoor.left.closed,
          rightDoor: this.positions.frontDoor.right.closed,
        },
        this.speed,
      )
      .easing(Easing.Cubic.InOut)
      .onUpdate(({ leftDoor, rightDoor }) => {
        this.frontDoorLeft.position.y = leftDoor;
        this.frontDoorRight.position.y = rightDoor;
      })
      .start();
  };

  private openBackDoor = (skipAnimation = false) => {
    if (isNil(this.backDoorLeft) || isNil(this.backDoorRight)) {
      return;
    }

    if (this.backDoorLeft.position.y === this.positions.backDoor.left.open) {
      return;
    }

    if (skipAnimation) {
      this.backDoorLeft.position.y = this.positions.backDoor.left.open;
      this.backDoorRight.position.y = this.positions.backDoor.right.open;
      return;
    }

    // Stop and clean up any existing door animation
    this.stopDoorAnimation();

    // Ensure main animation loop is running for tween updates
    this.startAnimation();

    this.doorAnimationTween = new Tween({
      leftDoor: this.backDoorLeft.position.y,
      rightDoor: this.backDoorRight.position.y,
    })
      .to(
        {
          leftDoor: this.positions.backDoor.left.open,
          rightDoor: this.positions.backDoor.right.open,
        },
        this.speed,
      )
      .easing(Easing.Cubic.InOut)
      .onUpdate(({ leftDoor, rightDoor }) => {
        this.backDoorLeft.position.y = leftDoor;
        this.backDoorRight.position.y = rightDoor;
      })
      .start();
  };

  private closeBackDoor = (skipAnimation = false) => {
    if (isNil(this.backDoorLeft) || isNil(this.backDoorRight)) {
      return;
    }

    if (this.backDoorLeft.position.y === this.positions.backDoor.left.closed) {
      return;
    }

    if (skipAnimation) {
      this.backDoorLeft.position.y = this.positions.backDoor.left.closed;
      this.backDoorRight.position.y = this.positions.backDoor.right.closed;
      return;
    }

    // Stop and clean up any existing door animation
    this.stopDoorAnimation();

    // Ensure main animation loop is running for tween updates
    this.startAnimation();

    this.doorAnimationTween = new Tween({
      leftDoor: this.backDoorLeft.position.y,
      rightDoor: this.backDoorRight.position.y,
    })
      .to(
        {
          leftDoor: this.positions.backDoor.left.closed,
          rightDoor: this.positions.backDoor.right.closed,
        },
        this.speed,
      )
      .easing(Easing.Cubic.InOut)
      .onUpdate(({ leftDoor, rightDoor }) => {
        this.backDoorLeft.position.y = leftDoor;
        this.backDoorRight.position.y = rightDoor;
      })
      .start();
  };

  private startPulseArrowAnimation = (arrow) => {
    if (!arrow) return;

    // Clear any existing pulse interval
    this.stopPulseArrowAnimation();

    let counter = 0;
    this.pulseInterval = setInterval(() => {
      // Check if component is still mounted
      if (!this.isComponentMounted || !arrow.children) {
        this.stopPulseArrowAnimation();
        return;
      }

      const selectedIndex = counter % 10;
      const childrenLength = arrow.children.length;

      for (let i = 0; i < childrenLength; i++) {
        const child = arrow.children[i];
        if (child) {
          this.changeMaterial(
            child,
            i === selectedIndex
              ? this.materials.pulseOn
              : this.materials.pulseOff,
          );
        }
      }
      counter++;
    }, 180);
  };

  private stopPulseArrowAnimation = () => {
    if (this.pulseInterval !== null) {
      clearInterval(this.pulseInterval);
      this.pulseInterval = null;
    }

    if (this.arrowDown?.children?.length > 0) {
      for (let i = 0; i < this.arrowDown.children.length; i++) {
        this.changeMaterial(
          this.arrowDown.children[i],
          this.materials.pulseOff,
        );
        this.changeMaterial(this.arrowUp.children[i], this.materials.pulseOff);
      }
    }
  };

  private moveUp = () => {
    if (isNil(this.arrowUp) || isNil(this.arrowDown)) {
      return;
    }

    this.stopPulseArrowAnimation();
    this.hideNode(this.arrowDown);
    this.showNode(this.arrowUp);
    this.startPulseArrowAnimation(this.arrowUp);
    this.startAnimateFloorsIndicator(ElevatorDirection.Down);
    this.movingDirection = ElevatorDirection.Down;
  };

  private moveDown = () => {
    if (isNil(this.arrowUp) || isNil(this.arrowDown)) {
      return;
    }

    this.stopPulseArrowAnimation();
    this.hideNode(this.arrowUp);
    this.showNode(this.arrowDown);
    this.startPulseArrowAnimation(this.arrowDown);
    this.startAnimateFloorsIndicator(ElevatorDirection.Up);
    this.movingDirection = ElevatorDirection.Up;
  };

  private stopMoving = () => {
    if (!isNil(this.arrowUp)) {
      this.hideNode(this.arrowUp);
    }
    if (!isNil(this.arrowDown)) {
      this.hideNode(this.arrowDown);
    }
    this.stopPulseArrowAnimation();
    this.stopAnimateFloorsIndicator(this.movingDirection);
  };

  private startAnimateFloorsIndicator = (direction: ElevatorDirection) => {
    if (isNil(this.floorSeparatorIndicator)) {
      return;
    }

    // If already easing in, don't start a new easing animation
    if (this.isEasingIn) {
      return;
    }

    // Stop any existing animation and reset flags
    if (this.floorAnimationTween !== null) {
      this.floorAnimationTween.stop();
      this.floorAnimationTween = null;
    }

    // Reset any ongoing easing out state
    if (this.isEasingOut) {
      this.isEasingOut = false;
    }

    // Reset continuous animation flag
    this.animateTheFloor = false;

    this.isEasingIn = true;
    let easingAmount = 50;

    if (direction === ElevatorDirection.Down) {
      easingAmount = -easingAmount;
    }

    // Easy in the floor animation before starting continuous movement
    this.floorAnimationTween = new Tween({
      topPos: this.floorSeparatorIndicator.position.z,
    })
      .to({
        topPos: this.floorSeparatorIndicator.position.z + easingAmount,
      })
      .easing(Easing.Cubic.In)
      .onUpdate(({ topPos }) => {
        this.floorSeparatorIndicator.position.z = topPos;
      })
      .onComplete(() => {
        this.isEasingIn = false;
        this.animateTheFloor = true;
        this.animateFloorsIndicator(direction);
      })
      .start();
  };

  private stopAnimateFloorsIndicator = (elevatorDirection: ElevatorDirection) => {
    if (isNil(this.floorSeparatorIndicator)) {
      return;
    }

    // If already easing out, don't start a new easing animation
    if (this.isEasingOut) {
      return;
    }

    if (this.floorAnimationTween !== null) {
      this.animateTheFloor = false;
      this.floorAnimationTween.stop();

      // Reset easing in flag if we were in that state
      if (this.isEasingIn) {
        this.isEasingIn = false;
      }

      this.isEasingOut = true;
      let easingAmount = 50;

      if (elevatorDirection === ElevatorDirection.Down) {
        easingAmount = -easingAmount;
      }

      // Easy out the floor animation
      this.floorAnimationTween = new Tween({
        topPos: this.floorSeparatorIndicator.position.z,
      })
        .to({
          topPos: this.floorSeparatorIndicator.position.z + easingAmount,
        })
        .easing(Easing.Cubic.Out)
        .onUpdate(({ topPos }) => {
          this.floorSeparatorIndicator.position.z = topPos;
        })
        .onComplete(() => {
          this.floorAnimationTween = null;
          this.isEasingOut = false;
        })
        .start();
    }
  };

  private animateFloorsIndicator = (elevatorDirection: ElevatorDirection) => {
    if (isNil(this.floorSeparatorIndicator)) {
      return;
    }

    // Don't start continuous animation if we're easing out or no longer supposed to animate or component unmounted
    if (!this.animateTheFloor || !this.isComponentMounted) {
      return;
    }

    let startPos: number;
    let endPos: number;
    let movementSpeed = this.floorHeight * 10;

    if (elevatorDirection === ElevatorDirection.Up) {
      if (this.floorSeparatorIndicator.position.z >= this.positions.floor.up) {
        startPos = this.positions.floor.down;
      }
      // If the floor separator indicator is in view, continue from there
      else {
        startPos = this.floorSeparatorIndicator.position.z;
        // Adjust speed based on distance
        movementSpeed =
          Math.abs(
            this.floorSeparatorIndicator.position.z - this.positions.floor.up,
          ) * 10;
      }
      endPos = this.positions.floor.up;
    } else if (elevatorDirection === ElevatorDirection.Down) {
      if (
        this.floorSeparatorIndicator.position.z <= this.positions.floor.down
      ) {
        startPos = this.positions.floor.up;
      }
      // If the floor separator indicator is in view, continue from there
      else {
        startPos = this.floorSeparatorIndicator.position.z;
        // Adjust speed based on distance
        movementSpeed =
          (this.floorSeparatorIndicator.position.z -
            this.positions.floor.down) *
          10;
      }
      endPos = this.positions.floor.down;
    }

    if (this.floorAnimationTween !== null) {
      this.floorAnimationTween.stop();
      this.floorAnimationTween = null;
    }

    this.floorAnimationTween = new Tween({
      topPos: startPos,
    })
      .to(
        {
          topPos: endPos,
        },
        movementSpeed,
      )
      .onUpdate(({ topPos }) => {
        this.floorSeparatorIndicator.position.z = topPos;
      })
      .onComplete(() => {
        // Restart the animation for continuous effect only if component is still mounted
        if (this.animateTheFloor === true && this.isComponentMounted) {
          this.animateFloorsIndicator(elevatorDirection);
        }
      })
      .start();
  };

  private hideNode = (node: THREE.Object3D) => {
    if (!this.isComponentMounted || isNil(node)) {
      return;
    }

    node.traverse((childNode) => {
      childNode.visible = false;
    });
  };

  private showNode = (node: THREE.Object3D) => {
    if (!this.isComponentMounted || isNil(node)) {
      return;
    }

    node.traverse((childNode) => {
      if (childNode.type === "LineSegments") {
        childNode.visible = false;
      } else {
        childNode.visible = true;
      }
    });
  };

  /**
   * Updates the error state based on current component conditions
   */
  private updateErrorState() {
    if (this.store === null) {
      this.errorState = getStoreUnavailableError(this.store?.state.locale);
      return;
    }

    if (this.store.state.error.hasError) {
      if (this.errorState.hasError) {
        this.errorState = {
          hasError: true,
          messageKey: this.store.state.error.messageKey,
          type: this.store.state.error.type,
        };
        this.updateErrorMessageElement();
        return;
      }

      this.errorState = {
        hasError: true,
        messageKey: this.store.state.error.messageKey,
        type: this.store.state.error.type,
      };

      if (!isNil(this.canvas)) {
        applyErrorMessage(
          this.canvas,
          getErrorMessageForDisplay(
            this.errorState.messageKey,
            this.store?.state.locale,
          ),
        );
      }

      this.clearTimer();
      return;
    }

    // No error detected
    if (this.errorState.hasError) {
      this.errorState = {
        hasError: false,
        messageKey: undefined,
        type: ErrorType.None,
      };
    }

    if (!isNil(this.canvas)) {
      clearErrorMessage(this.canvas);
    }
    this.setTimer();
  }

  private updateErrorMessageElement() {
    if (isNil(this.canvas)) return;
    const existingError = this.canvas.querySelector(".error-message");
    if (existingError !== null) {
      existingError.textContent = getErrorMessageForDisplay(
        this.errorState.messageKey,
        this.store?.state.locale,
      );
    }
  }

  private syncSensorDisplayState(
    sensorData: ILiftStatusSensorDataResponse | undefined | null,
  ) {
    if (sensorData === undefined || sensorData === null) {
      return;
    }

    const sensorValues = sensorData.values;
    let lastUpdate = 0;

    SCENE_SENSOR_IDS.forEach((value) => {
      lastUpdate = dayjs(sensorData.updated[value]).isAfter(dayjs(lastUpdate))
        ? sensorData.updated[value]
        : lastUpdate;
    });

    if (lastUpdate > 0) {
      this.lastUpdate = lastUpdate;
    }

    const movement = sensorValues[SENSOR.MovementState];
    if (movement !== undefined) {
      this.movementState = movement;
    }

    const currentStop = sensorValues[SENSOR.CurrentStop];
    if (currentStop !== undefined) {
      this.currentStop = currentStop;
    }

    const lastPassed = sensorValues[SENSOR.LastPassedStop];
    if (lastPassed !== undefined) {
      this.lastPassedStop = lastPassed;
    }
  }

  private processSensorUpdate = (
    sensorData: ILiftStatusSensorDataResponse,
    initial = false,
  ) => {
    // Prevent processing if component is not mounted, except for initial snapshot
    if (!this.isComponentMounted && !initial) {
      return;
    }

    const sensorValues = sensorData.values;

    // Only update error state if not initial load to avoid excessive DOM operations
    if (!initial) {
      this.updateErrorState();
    }

    if (this.errorState.hasError) {
      return;
    }

    if (!initial) {
      let lastUpdate = 0;

      SCENE_SENSOR_IDS.forEach((value) => {
        lastUpdate = dayjs(sensorData.updated[value]).isAfter(dayjs(lastUpdate))
          ? sensorData.updated[value]
          : lastUpdate;
      });

      this.lastUpdate = lastUpdate;
    }

    for (const sensorId in sensorValues) {
      // Elevator state (movement)
      switch (sensorId) {
        case `${SENSOR.MovementState}`:
          switch (sensorValues[sensorId]) {
            case MovementState.Down:
              if (this.movementState !== MovementState.Down) {
                this.moveDown();
              }
              break;
            case MovementState.Up:
              if (this.movementState !== MovementState.Up) {
                this.moveUp();
              }
              break;
            default:
              this.stopMoving();
          }
          if (!initial) {
            this.movementState = sensorValues[sensorId];
          }
          break;

        // Current stop place
        case `${SENSOR.CurrentStop}`:
          if (!initial) {
            this.currentStop = sensorValues[sensorId];
          }
          break;

        // Front door
        case `${SENSOR.FrontDoorPosition}`:
          switch (sensorValues[sensorId]) {
            case DoorPosition.Closed:
            case DoorPosition.Locked:
              this.closeFrontDoor(true);
              break;
            case DoorPosition.Closing:
              this.closeFrontDoor(false);
              break;
            case DoorPosition.Open:
              this.openFrontDoor(true);
              break;
            case DoorPosition.Opening:
              this.openFrontDoor(false);
              break;
            default:
              this.closeFrontDoor(true);
          }
          break;

        // Rear door
        case `${SENSOR.RearDoorPosition}`:
          switch (sensorValues[sensorId]) {
            case DoorPosition.Closed:
            case DoorPosition.Locked:
              this.closeBackDoor(true);
              break;
            case DoorPosition.Closing:
              this.closeBackDoor(false);
              break;
            case DoorPosition.Open:
              this.openBackDoor(true);
              break;
            case DoorPosition.Opening:
              this.openBackDoor(false);
              break;
            default:
              this.closeBackDoor(true);
          }
          break;

        // Last passed
        case `${SENSOR.LastPassedStop}`:
          if (!initial) {
            this.lastPassedStop = sensorValues[sensorId];
          }
          break;
      }
    }
  };

  private hasBackDoor = (hasBackDoor: boolean) => {
    if (hasBackDoor) return;

    // Clear any existing timeout
    if (this.hasBackDoorTimeout !== null) {
      clearTimeout(this.hasBackDoorTimeout);
      this.hasBackDoorTimeout = null;
    }

    // Wait until the scene is loaded (nodes are null after cleanupScene)
    if (
      isNil(this.backDoor) ||
      isNil(this.elevatorBackWall) ||
      isNil(this.elevatorBackWallBeam)
    ) {
      // Only retry if component is still mounted
      if (this.isComponentMounted) {
        // Use arrow function to minimize closure scope
        this.hasBackDoorTimeout = setTimeout(() => {
          // Re-check mounted state inside timeout
          if (this.isComponentMounted) {
            this.hasBackDoor(hasBackDoor);
          }
        }, 100);
      }
      return;
    }

    this.hideNode(this.backDoor);
    this.showNode(this.elevatorBackWall);
    this.showNode(this.elevatorBackWallBeam);
  };

  private setTimer() {
    // Clear any existing timer first
    this.clearTimer();

    // The timer is used to keep the relative timestamps and movement state fresh
    // Use a weak reference to avoid closure memory leaks
    this.timer = window.setInterval(() => {
      // Only process if component is still mounted
      if (!this.isComponentMounted) {
        this.clearTimer();
        return;
      }

      // Access store data only when needed to minimize closure scope
      const sensorData = this.store?.state?.currentSensorData;
      if (sensorData !== undefined) {
        this.processSensorUpdate(sensorData);
      }
    }, 1000);
  }

  private clearTimer() {
    window.clearInterval(this.timer);
    this.timer = null;
  }

  connectedCallback() {
    this.isComponentMounted = true;
    this.setTimer();
    this.startAnimation();
  }

  private cleanupScene() {
    // Dispose of existing renderer
    if (!isNil(this.renderer)) {
      this.renderer.dispose();
      this.renderer = null;
    }

    // Dispose of existing scene resources
    if (!isNil(this.scene)) {
      // Dispose geometries and materials
      this.scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          if (object.geometry) {
            object.geometry.dispose();
          }
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach((material) => material.dispose());
            } else {
              object.material.dispose();
            }
          }
        }
      });
      this.scene.clear();
      this.scene = null;
    }

    // Dispose of materials
    Object.values(this.materials).forEach((material) => {
      if (material !== undefined && typeof material.dispose === "function") {
        material.dispose();
      }
    });

    // Clear references to Three.js objects
    this.camera = null;
    this.elevator = null;
    this.arrowDown = null;
    this.arrowUp = null;
    this.backDoor = null;
    this.backDoorLeft = null;
    this.backDoorRight = null;
    this.elevatorBackWall = null;
    this.elevatorBackWallBeam = null;
    this.frontDoorLeft = null;
    this.frontDoorRight = null;
    this.floorSeparatorIndicator = null;
  }

  disconnectedCallback() {
    this.isComponentMounted = false;

    // Stop all animations and timers
    this.stopAnimation();
    this.clearTimer();

    // Clear hasBackDoor timeout
    if (this.hasBackDoorTimeout !== null) {
      clearTimeout(this.hasBackDoorTimeout);
      this.hasBackDoorTimeout = null;
    }

    // Unsubscribe from store changes
    if (this.storeUnsubscribe !== null) {
      this.storeUnsubscribe();
      this.storeUnsubscribe = null;
    }

    // Stop and cleanup tweens
    if (this.floorAnimationTween !== null) {
      this.floorAnimationTween.stop();
      this.floorAnimationTween = null;
    }
    this.stopDoorAnimation();

    // Reset animation flags
    this.isEasingOut = false;
    this.isEasingIn = false;
    this.animateTheFloor = false;

    // Clear pulse interval
    if (this.pulseInterval !== null) {
      clearInterval(this.pulseInterval);
      this.pulseInterval = null;
    }

    if (this.resizeObserver !== null) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.resizeFrameId !== null) {
      cancelAnimationFrame(this.resizeFrameId);
      this.resizeFrameId = null;
    }

    // Clean up Three.js resources
    this.cleanupScene();
  }

  async componentWillLoad() {
    dayjs.extend(relativeTime);

    this.store = await getStore(this.idKey);
    watchStoreLocale(this, this.store, () => {
      this.updateErrorMessageElement();
      if (isNil(this.canvas) || this.store === null) return;
      const failedToLoad = this.canvas.querySelector(".failed-to-load-error");
      if (!isNil(failedToLoad)) {
        failedToLoad.textContent = this.store.t("ui:scene.failed_to_load");
      }
    });

    this.updateErrorState();
    this.syncSensorDisplayState(this.store.state.currentSensorData);

    const sensorDataUnsubscribe = this.store.onChange(
      "currentSensorData",
      (sensorData) => {
        this.processSensorUpdate(sensorData);
      },
    );

    const errorUnsubscribe = this.store.onChange("error", () => {
      this.updateErrorState();
    });

    this.storeUnsubscribe = () => {
      sensorDataUnsubscribe();
      errorUnsubscribe();
    };
  }

  componentDidLoad() {
    this.initializeScene();
    this.startAnimation();
    this.observeResize();

    this.hasBackDoor(this.store.state.hasBackDoor);
    this.updateErrorMessageElement();
    this.processSensorUpdate(this.store.state.currentSensorData, true);
  }

  componentDidUpdate() {
    // Only start animation if not already running
    if (!this.isAnimating) {
      this.startAnimation();
    }
  }

  render() {
    const effectiveLastUpdate =
      this.lastUpdate ||
      this.store?.state.currentSensorData?.lastUpdate ||
      0;

    return (
      <Host>
        <div
          class="canvas"
          ref={(el) => {
            this.canvas = el;
          }}
        >
          <ls-indicator
            class="current-floor"
            sensor-id={SENSOR.CurrentStop}
            id-key={this.idKey}
          ></ls-indicator>
          <ls-indicator
            class="last-passed-floor"
            sensor-id={SENSOR.LastPassedStop}
            id-key={this.idKey}
          ></ls-indicator>
          <div class="last-update">
            {getSafeRelativeTime(
              effectiveLastUpdate,
              this.store?.state.locale
            )}
          </div>
        </div>
        <slot></slot>
      </Host>
    );
  }
}
